import { Controller } from "@hotwired/stimulus"

/**
 * TOC (Table of Contents) Highlighter Controller
 *
 * Implements sticky TOC with automatic highlighting based on scroll position.
 * Uses IntersectionObserver API for performant scroll detection.
 *
 * Features:
 * - Sticky TOC sidebar (desktop only)
 * - Auto-highlight current section
 * - Smooth scroll to sections on click
 * - Mobile-responsive (hides on mobile, shows on desktop)
 *
 * Usage:
 *   <nav data-controller="toc-highlighter" data-toc-highlighter-target="toc">
 *     <a href="#section-1" data-toc-highlighter-target="link">Section 1</a>
 *     <a href="#section-2" data-toc-highlighter-target="link">Section 2</a>
 *   </nav>
 *
 *   <section id="section-1" data-toc-highlighter-target="section">...</section>
 *   <section id="section-2" data-toc-highlighter-target="section">...</section>
 *
 * @class TocHighlighterController
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["toc", "link", "section"]

  /**
   * Initialize IntersectionObserver on controller connect
   * Sets up scroll detection for all section elements
   */
  connect() {
    // IntersectionObserver options
    // rootMargin: Trigger when section is 20% into viewport
    // threshold: Fire callback when 10% of section is visible
    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: "-20% 0px -70% 0px", // Top 20% to bottom 30% zone
      threshold: [0, 0.1, 0.5, 1.0] // Multiple thresholds for accuracy
    }

    // Create IntersectionObserver
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.highlightLink(entry.target.id)
        }
      })
    }, observerOptions)

    // Observe all section targets
    this.sectionTargets.forEach(section => {
      this.observer.observe(section)
    })

    // Add smooth scroll behavior to all links
    // Bound handler is stored so disconnect() can remove the same reference
    this.boundSmoothScroll = this.smoothScroll.bind(this)
    this.linkTargets.forEach(link => {
      link.addEventListener("click", this.boundSmoothScroll)
    })

    // Short trailing sections never enter the observer band — force-highlight
    // the last section when the page is scrolled to the bottom
    this.boundBottomCheck = this.highlightLastAtBottom.bind(this)
    window.addEventListener("scroll", this.boundBottomCheck, { passive: true })
  }

  /**
   * Cleanup: Disconnect observer on controller disconnect
   */
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }

    // Remove event listeners
    this.linkTargets.forEach(link => {
      link.removeEventListener("click", this.boundSmoothScroll)
    })
    window.removeEventListener("scroll", this.boundBottomCheck)
  }

  /**
   * Highlight the last section once the viewport reaches the document bottom
   */
  highlightLastAtBottom() {
    if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 2) return

    const lastSection = this.sectionTargets[this.sectionTargets.length - 1]
    if (lastSection) this.highlightLink(lastSection.id)
  }

  /**
   * Highlight the corresponding TOC link for the given section ID
   * @param {string} sectionId - The ID of the section to highlight
   */
  highlightLink(sectionId) {
    // Remove active class from all links
    this.linkTargets.forEach(link => {
      link.classList.remove("bg-butter-200", "font-bold", "text-charcoal-900")
      link.classList.add("text-charcoal-600")
      link.setAttribute("aria-current", "false")
    })

    // Find and highlight the matching link
    const activeLink = this.linkTargets.find(link =>
      link.getAttribute("href") === `#${sectionId}`
    )

    if (activeLink) {
      activeLink.classList.remove("text-charcoal-600")
      activeLink.classList.add("bg-butter-200", "font-bold", "text-charcoal-900")
      activeLink.setAttribute("aria-current", "location")

      // Scroll TOC to show active link (if sticky TOC is scrollable)
      activeLink.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  /**
   * Smooth scroll to section when TOC link is clicked
   * @param {Event} event - Click event
   */
  smoothScroll(event) {
    event.preventDefault()

    const href = event.currentTarget.getAttribute("href")
    const targetSection = document.querySelector(href)

    if (targetSection) {
      // Calculate offset (account for sticky header if exists)
      const headerOffset = 80 // Adjust based on your header height
      const elementPosition = targetSection.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      // Smooth scroll
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      })

      // Update URL hash without adding a history entry — pushState(null,...) would
      // create entries without Turbo restoration state (back/forward desync)
      history.replaceState(history.state, "", href)

      // Focus the section for accessibility (preventScroll keeps the smooth scroll intact)
      targetSection.setAttribute("tabindex", "-1")
      targetSection.focus({ preventScroll: true })
    }
  }
}
